import { Button, CircularProgress, Dialog, Stack, Typography } from '@mui/material';

/**
 * Standard confirmation dialog. Replaces the four hand-copied confirm
 * modals (reset password, CredBuild toggle, logout, banner delete).
 *
 * Props:
 * - open, onClose
 * - title, body (string or node)
 * - confirmLabel, cancelLabel
 * - onConfirm
 * - loading: disables both buttons, spinner on confirm
 * - destructive: renders the confirm button in the error color
 * - disableBackdropClose: block closing via backdrop/escape (logout-style)
 */
export default function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  loading = false,
  destructive = false,
  disableBackdropClose = false,
}) {
  const handleClose = (event, reason) => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    onClose?.(event, reason);
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { maxWidth: 472, width: '100%', p: 3 } }}>
      <Stack gap={1.5} sx={{ textAlign: 'center' }}>
        <Typography variant="h4">{title}</Typography>
        {typeof body === 'string' ? (
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        ) : (
          body
        )}
        <Stack direction="row" gap={1.25} sx={{ mt: 1.5 }}>
          <Button variant="outlined" fullWidth onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            fullWidth
            onClick={onConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirmLabel}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
